/**
 * Tenant-transition isolation for in-memory `lastFinalBilling`.
 *
 * lastFinalBilling has no restaurant_id. It is valid only while the committed
 * public tenant is unchanged. Empty/loading restaurant ids must not clear it
 * (same-tenant bootstrap / refetch). A different committed id must clear it
 * before the next tenant's checkout can mount.
 *
 * PublicTenantProvider also clears `activeUnpaidOrder` on the same plan
 * (`clearFinalBilling === true`). That leftover has a separate Redux lifecycle
 * and DTO; this module does not own it.
 */

export type FinalBillingTenantPlan = {
  nextCommitted: string | null
  clearFinalBilling: boolean
}

/**
 * Authoritative restaurantId was resolved (bootstrap success / legacy opt-in).
 * `incomingRestaurantId` must already be trimmed by the caller or is trimmed here.
 */
export function planFinalBillingOnCommittedRestaurantId(
  committedRestaurantId: string | null,
  incomingRestaurantId: string
): FinalBillingTenantPlan {
  const incoming = incomingRestaurantId.trim()
  if (!incoming) {
    return { nextCommitted: committedRestaurantId, clearFinalBilling: false }
  }

  const committed = committedRestaurantId?.trim() || null
  if (committed == null) {
    return { nextCommitted: incoming, clearFinalBilling: false }
  }
  if (committed === incoming) {
    return { nextCommitted: committed, clearFinalBilling: false }
  }
  return { nextCommitted: incoming, clearFinalBilling: true }
}

/**
 * Authoritative tenant was lost (unavailable / error / missing id after a commit).
 * First-visit fail-closed (never committed) does not clear.
 */
export function planFinalBillingOnTenantLost(
  committedRestaurantId: string | null
): FinalBillingTenantPlan {
  const committed = committedRestaurantId?.trim() || null
  if (committed == null) {
    return { nextCommitted: null, clearFinalBilling: false }
  }
  return { nextCommitted: null, clearFinalBilling: true }
}
